import React, { useContext, useEffect, useState } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import StaffDetailHorizontalTab from "./staffDetailHorizontalTab";
import { useDispatch } from "react-redux";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import AddEditStaffDialog from "./addEditStaffDilog";
import { useMutation } from "@apollo/client";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteConfirmationDialog from "./deleteConfirmationDialog";
import AddIcon from "@mui/icons-material/Add";
import { Permissions, hasPermission } from "../../../util/accessControl";
import { UserContext } from "../../auth/auth-context";
import { useSelectedBusiness } from "../../../redux/store";
import { Staff } from "../../../types";
import {
  DELETE_STAFF,
  POST_STAFF,
  UPDATE_PAYPAL_SUBSCRIPTION,
  UPDATE_STAFF,
} from "../../../graphql/mutations";
import {
  deleteStaff,
  updateStaff,
} from "../../../redux/actions/businessActions";
import UpgradePlan from "../../../util/upgrade/upgradePlan";
import { StaffStatus } from "../../../util/utils";
export default function StaffListVerticalTab() {
  const userContext = useContext(UserContext);
  const providerUser = userContext.providerUser;

  const dispatch = useDispatch();
  const selectedBusiness = useSelectedBusiness();
  const businessStaffs = selectedBusiness?.businessStaffs;
  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null); // Initialize the state here
  const [value, setValue] = React.useState(0);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [deleteConfirmationName, setDeleteConfirmationName] = useState("");
  const [checkPlanNeeded, setCheckPlanNeeded] = useState(false);
  const standardPlanRequired = 2;
  const staffs = selectedBusiness?.businessStaffs;
  const businessId = selectedBusiness?.businessBasic.id;

  const [updatePaypalSubscription] = useMutation(UPDATE_PAYPAL_SUBSCRIPTION);
  const [deleteStaffDB] = useMutation(DELETE_STAFF);
  const [postStaff] = useMutation(POST_STAFF);
  const [updateStaffDB] = useMutation(UPDATE_STAFF);
  const [isDropdown, setIsDropdown] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Function to handle screen size changes
  const handleResize = () => {
    // Check the window width and set isDropdown accordingly
    if (window.innerWidth <= 768) {
      setIsDropdown(true);
    } else {
      setIsDropdown(false);
    }
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (
      !selectedStaff ||
      !staffs ||
      !staffs.length ||
      !staffs.some((staff) => staff.id === selectedStaff.id)
    ) {
      const topStaff = staffs && staffs.length > 0 ? staffs[0] : null;
      if (topStaff) {
        setValue(0);
        setSelectedStaff(topStaff);
      }
    }
  }, []);

  const handleMoreVertClick = (bookingType: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(bookingType.currentTarget);
  };

  const handleMoreVertClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = (selectedStaff: Staff | null) => {
    setDeleteConfirmationName(selectedStaff?.user?.username ?? "");
    setSelectedStaff(selectedStaff);
    setDeleteConfirmationOpen(true);
    handleMoreVertClose();
  };

  const handleDeleteConfirmationClose = () => {
    setDeleteConfirmationName(selectedStaff?.user?.username ?? "");
    setDeleteConfirmationOpen(false);
  };

  const handleDialogClose = () => {
    setErrorDialogOpen(false); // Close the error dialog
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    businessStaffs && setSelectedStaff(businessStaffs[newValue]);
  };
  const handleOpenAddDialog = (staff?: Staff) => {
    const effectivePlan = Number(providerUser.planId);
    // skip validating for exmptedRequiredPlanId
    if (!staff && effectivePlan < standardPlanRequired) {
      // Set state to show the UpgradePlan component
      setCheckPlanNeeded(true);
      return;
    }
    setSelectedStaff(staff || null);
    setIsEditMode(!!staff); // Set isEditMode to true when staff is provided (edit mode)
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setSelectedStaff(null);
  };

  const renderErrorDialog = () => (
    <Dialog open={errorDialogOpen} onClose={handleDialogClose}>
      <DialogTitle>Validation Error</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Cannot delete or update the Admin status for the last admin user.
          Please add another staff member and assign them the admin role before
          performing this operation.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose} color="primary">
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );

  const handleAddStaff = async (newStaff: Staff) => {
    const { user, role, ...rest } = newStaff;
    const email = user?.email || "";
    const name = user?.username || "";

    try {
      const res = await postStaff({
        variables: {
          businessId: businessId,
          businessStaff: {
            ...rest,
            roleId: newStaff?.role?.id,
            email: email,
            name: name,
          },
        },
      });

      const staffData = res.data?.createStaff;

      if (staffData) {
        dispatch(updateStaff(businessId ?? -1, staffData));

        handleCloseAddDialog();

        return staffData;
      }
    } catch (error) {
      console.error("Error adding staff:", error);
    }

    return null;
  };

  const handleDeleteStaff = () => {
    // Check if there is only one admin user left
    const adminUsers = staffs?.filter((s: Staff) => s?.role?.name === "Admin");
    if (
      adminUsers?.length === 1 &&
      adminUsers[0].id === selectedStaff?.id &&
      selectedStaff?.role?.name === "Admin"
    ) {
      handleDeleteConfirmationClose(); // Close the delete confirmation dialog
      setErrorDialogOpen(true); // Open the error dialog
      return;
    }
    handleDeleteConfirmationClose();

    // TODO: Verify deletion has been successful before clearing it from the store.
    deleteStaffDB({
      variables: { staffId: selectedStaff?.id },
    }).then((res: any) => {
      updatePaypalSubscription({
        variables: {
          subscriptionID: providerUser.subscriptionID,
          quantity: -1,
        },
      });
      const deletedStaff = res.data?.deleteStaff;
      if (deletedStaff) {
        const newSelectedStaff = staffs?.filter(
          (s: Staff) => s.id !== selectedStaff?.id
        )[0];

        setSelectedStaff(newSelectedStaff || null);
        businessId && dispatch(deleteStaff(businessId, deletedStaff?.id ?? -1));
      }
    });
  };

  const handleUpdateStaff = (staff: Staff) => {
    // Check if there is only one admin user left
    const adminUsers = staffs?.filter((s: Staff) => s?.role?.name === "Admin");
    if (
      adminUsers?.length === 1 &&
      adminUsers[0].id === staff.id &&
      selectedStaff?.role?.name === "Admin" &&
      staff.role?.name !== "Admin" // to check if this is a change of role. no role change allowed for the last admin
    ) {
      setErrorDialogOpen(true); // Open the error dialog for last admin user update
      return;
    }

    updateStaffDB({
      variables: {
        staffId: staff.id,
        businessStaff: {
          email: staff?.user?.email,
          name: staff?.user?.username,
          roleId: staff.role?.id,
          url: staff.url,
        },
      },
    })
      .then((res: any) => {
        const updatedStaff = res.data?.updateStaff;
        businessId &&
          updateStaff &&
          dispatch(updateStaff(businessId, updatedStaff));

        // Refresh selected staff from updated Redux store
        const updatedIndex =
          staffs?.findIndex((s) => s.id === updatedStaff.id) ?? -1;
        if (updatedIndex !== -1) {
          const refreshedStaff = staffs?.[updatedIndex];
          setSelectedStaff(refreshedStaff || null);
          setValue(updatedIndex);
        }
      })
      .catch((error: any) => {
        console.error("Error updating staff:", error);
      });
    handleCloseAddDialog();
  };

  const handleSaveUpdateStaff =
    selectedStaff && isEditMode ? handleUpdateStaff : handleAddStaff;

  const currentLoginStaff = businessStaffs?.find(
    (staff) => Number(staff.id) === providerUser.staffId
  );

  const userPermissions = currentLoginStaff?.role?.permissions || [];

  const requiredEditCalendarPermission = Permissions.Calendar.EDIT;
  const canEditCalendar = hasPermission(
    userPermissions,
    requiredEditCalendarPermission,
    providerUser.staffId,
    Number(selectedStaff?.id)
  );

  const requiredEditBookingTypePermission = Permissions.Calendar.EDIT;
  const canEditBookingType = hasPermission(
    userPermissions,
    requiredEditBookingTypePermission,
    providerUser.staffId,
    Number(selectedStaff?.id)
  );

  const requiredAddPermission = Permissions.Staff.ADD;
  const canAddStaff = hasPermission(
    userPermissions,
    requiredAddPermission,
    providerUser.staffId,
    Number(selectedStaff?.id)
  );

  const requiredImagePermission = Permissions.Staff.EDIT_IMAGE_OWN;
  const canEditImageStaff = hasPermission(
    userPermissions,
    requiredImagePermission,
    providerUser.staffId,
    Number(selectedStaff?.id)
  );

  const requiredIntegerationPermission =
    Permissions.Staff.EDIT_INTEGERATION_OWN;
  const canEditIntegerationStaff = hasPermission(
    userPermissions,
    requiredIntegerationPermission,
    providerUser.staffId,
    Number(selectedStaff?.id)
  );

  const requiredEditPermission = Permissions.Staff.EDIT;
  const canEditStaff = hasPermission(
    userPermissions,
    requiredEditPermission,
    providerUser.staffId,
    Number(selectedStaff?.id)
  );

  const requiredDeletePermission = Permissions.Staff.DELETE;
  const canDeleteStaff = hasPermission(
    userPermissions,
    requiredDeletePermission,
    providerUser.staffId,
    Number(selectedStaff?.id)
  );

  return (
    <>
      {checkPlanNeeded && (
        <UpgradePlan
          featureName="Add staff"
          requiredPlanId={standardPlanRequired}
          applyExemption={false}
          onClose={() => setCheckPlanNeeded(false)}
        />
      )}
      {renderErrorDialog()} {/* Render the error dialog */}
      <div className="content-header">
        <div className="content-head">
          <div className="top">
            <div className="flex">
              <Typography variant="h5" sx={{ marginBottom: 2 }}>
                Staffs
              </Typography>
            </div>
            <div>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleOpenAddDialog()}
                disabled={!canAddStaff}
              >
                Add Staff
              </Button>

              <IconButton
                aria-label="more"
                aria-controls={`actions-menu`}
                aria-haspopup="true"
                onClick={(bookingType) =>
                  setAnchorEl(bookingType.currentTarget)
                }
                style={{ position: "absolute", top: 0, left: 0 }}
              >
                <MoreVertIcon />
              </IconButton>

              <IconButton
                aria-label="more"
                aria-haspopup="true"
                onClick={handleMoreVertClick}
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleMoreVertClose}
              >
                <MenuItem
                  onClick={() =>
                    handleDeleteClick(
                      businessStaffs ? businessStaffs[value] : null
                    )
                  }
                  disabled={!canDeleteStaff}
                >
                  Delete
                </MenuItem>
                <MenuItem
                  disabled={!canEditStaff}
                  onClick={() =>
                    handleOpenAddDialog(businessStaffs && businessStaffs[value])
                  }
                >
                  Edit
                </MenuItem>
              </Menu>
            </div>
          </div>
          <div>
            {" "}
            {isDropdown && (
              <Box sx={{ width: "100%", mt: 3 }}>
                <DropdownTabs
                  value={value}
                  onChange={handleTabChange}
                  options={businessStaffs || []}
                />
              </Box>
            )}{" "}
          </div>
        </div>
      </div>
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          margin: 5,
        }}
      >
        <AddEditStaffDialog
          open={openAddDialog}
          handleClose={handleCloseAddDialog}
          handleSaveUpdateStaff={handleSaveUpdateStaff}
          staff={isEditMode ? selectedStaff : null}
        />
        {isDropdown ? null : (
          <Tabs
            orientation="vertical"
            variant="scrollable"
            value={value}
            onChange={handleTabChange}
            aria-label="Vertical tabs example"
            sx={{
              borderRight: 1,
              borderColor: "divider",
              marginRight: "10px",
              paddingRight: "10px",
              paddingLeft: "10px",
            }}
          >
            {businessStaffs?.map((myStaff: Staff, index: number) => (
              <Tab
                key={myStaff.id}
                value={index}
                label={
                  Number(myStaff.status) === StaffStatus.PENDING ? (
                    <span>
                      {myStaff?.user?.username} -{" "}
                      <span style={{ textTransform: "lowercase" }}>
                        pending
                      </span>
                    </span>
                  ) : (
                    myStaff?.user?.username
                  )
                }
                sx={{
                  backgroundColor: value === index ? "#D3D3D3" : "transparent",
                  padding: "10px",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              />
            ))}
          </Tabs>
        )}

        {businessStaffs
          ? businessStaffs[value] && (
              <StaffDetailHorizontalTab
                isStaffCanEditItsImage={!canEditImageStaff}
                isStaffCanEditItsEmailIntegeration={!canEditIntegerationStaff}
                isStaffDisabled={!canEditStaff}
                isCalenderDisabled={!canEditCalendar}
                isBookingTypeDisabled={!canEditBookingType}
                businessStaff={businessStaffs[value]}
              />
            )
          : ""}
      </Box>
      <DeleteConfirmationDialog
        open={deleteConfirmationOpen}
        staffName={deleteConfirmationName}
        handleClose={handleDeleteConfirmationClose}
        handleDelete={handleDeleteStaff}
      />
    </>
  );
}

// DropdownTabs component to render the tabs as a dropdown
function DropdownTabs({
  value,
  onChange,
  options,
}: {
  value: number;
  onChange: (event: React.SyntheticEvent, newValue: number) => void;
  options: Staff[];
}) {
  return (
    <Box sx={{ width: "100%", mb: 2 }}>
      <Select
        id="responsive-dropdown"
        value={value}
        onChange={(e: SelectChangeEvent<number>) =>
          onChange(e as React.SyntheticEvent, e.target.value as number)
        }
        fullWidth
      >
        {options.map((staff: Staff, index: number) => (
          <MenuItem key={staff.id} value={index}>
            {staff?.user?.username}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}
